import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { FieldValues, FormProvider, FormState, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import useRegistration from '../../../../../hooks/useRegistration';
import { EventRegistration, RegistrationField } from '../../../../../interfaces/interfaces';
import { setEventRegistration } from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { getLimitedRegistrationFieldsData, updateRegistration } from '../../../../../services/services';
import VyButton from '../../../../common/VyButton';
import { RegistrationFormField } from '../../../signup/RegistrationFormField';

interface ProfileRegistrationFieldsProps {
  passedRegistration?: EventRegistration;
  onSubmit?: () => void;
  textFieldVariant?: 'standard' | 'outlined' | undefined;
  autoSave?: boolean;
  onFormStateChange?: (formState: FormState<FieldValues>) => void;
  textColor?: string;
  showAllRegistrationFields?: boolean;
}
export const ProfileRegistrationFields = ({
  passedRegistration,
  onSubmit,
  textFieldVariant = 'standard',
  autoSave,
  onFormStateChange,
  textColor,
  showAllRegistrationFields,
}: ProfileRegistrationFieldsProps) => {
  const registration = passedRegistration ?? useSelector((state) => state.common.eventRegistration);
  const user = useSelector((state) => state.common.user);
  const event = useSelector((state) => state.common.event ?? state.admin.event);
  const [eventRegistrationFields, setEventRegistrationFields] = useState<RegistrationField[]>();

  const { isCurrentFieldVisible } = useRegistration();

  const { t } = useTranslation();

  const defaultValues = registration?.registrationFields?.reduce((obj: any, item: any) => {
    return {
      ...obj,
      [item?.field?._id]: item.value,
    };
  }, {});

  const methods = useForm({
    defaultValues,
  });

  const dispatch = useDispatch();

  const [limitedFieldsCount, setLimitedFieldsCount] = useState<{ _id: string; value: string; count: number }[]>();

  const fetchLimitedFieldsData = async () => {
    if (!event) return;
    const { data } = await getLimitedRegistrationFieldsData(event?._id);
    setLimitedFieldsCount(data);
  };

  // DEBUG investigate whether this was fixed by 9820
  // useEffect(() => {
  //   if (methods.formState.isDirty === true && autoSave === true) {
  //     methods.handleSubmit(submit)();
  //   }
  // }, [methods.formState.isDirty]);

  const [requiredFieldIds, setRequiredFieldIds] = useState<Map<string, string[]>>(new Map());

  function filterRegistrationFields(field: RegistrationField) {
    if (!registration || !registration.registrationFields) return false;

    const existsInAltSignUp = registration.signup && registration.signup?.registrationFields?.findIndex((f: RegistrationField) => f._id === field._id) !== -1;

    if (showAllRegistrationFields) return true;

    return (
      (field.isHidden !== true || existsInAltSignUp) &&
      (!registration.signup || existsInAltSignUp) &&
      !field.datetime_deleted &&
      field.showInUserProfile !== false
    );
  }

  function filterPossibleRegistrationFields(field: RegistrationField) {
    if (!registration) return;
    return registration.registrationFields?.findIndex((f) => f.field?._id === field._id) === -1;
  }

  async function submit(data: any) {
    const loadingToast = toast.loading(t('toasts:saving-changes'));

    /**
     * If we have an email field in the registration fields
     * this is not in the props as we only have an email key. Therefore we append it with
     * the email fields value.
     */
    const emailFieldId = registration?.possibleRegistrationFields?.find((f) => f.isEmailField)?._id;
    if (data.email && emailFieldId) {
      data[emailFieldId] = data.email;
    }

    const registrationFields: any[] = [];

    try {
      registration?.registrationFields
        .filter(({ field }) => filterRegistrationFields(field))
        .map(({ field }) => {
          const key = field._id;

          const isRequired = Array.from(requiredFieldIds.values()).flat().includes(key) && field.isRequired && field.canEditInProfile;

          if (isRequired && (!data[key] || (field.fieldType === 'checkbox' && data[key] === 'false'))) throw new Error(`Field missing ${field.label}`);
          else data[key] !== undefined && registrationFields.push({ field: field._id, value: data[key] });
        });

      registration?.possibleRegistrationFields
        .filter((o) => filterRegistrationFields(o))
        .filter((o) => filterPossibleRegistrationFields(o))
        .map((field) => {
          const key = field._id;

          const isRequired = Array.from(requiredFieldIds.values()).flat().includes(key) && field.isRequired && field.canEditInProfile;
          if (isRequired && (!data[key] || (field.fieldType === 'checkbox' && data[key] === 'false'))) throw new Error(`Field missing ${field.label}`);
          else data[key] !== undefined && registrationFields.push({ field: field._id, value: data[key] });
        });
    } catch (err) {
      console.error(err);
      return;
    }

    try {
      if (registration) {
        const updated = await updateRegistration(registration._id, {
          registrationFields: registrationFields.map((reg) => ({ field: reg.field, value: reg.value })),
        });

        onSubmit?.() ?? dispatch(setEventRegistration(updated.data));
        //DEBUG investigate regarding 9820
        const resetObject = updated.data.registrationFields?.reduce((obj: any, item: any) => {
          return {
            ...obj,
            [item?.field?._id]: item.value,
          };
        }, {});
        methods.reset(resetObject);
        toast.success(t('toasts:success'));
      }
    } catch (e) {
      console.error(e);
    }

    toast.dismiss(loadingToast);
  }

  function getLimitedFieldsValuesCount(field: RegistrationField, currentValue: string) {
    if (field.fieldType !== 'limited') return [];
    if (!limitedFieldsCount) return [];

    const values = limitedFieldsCount.filter((f) => f._id === field._id);

    /**
     * We do a workaround that is special for the profile.
     *
     * The issue is that if we have a limited field such as 'Slot2' that is booked out:
     * If the current registration has slot2 booked we would still show that it is booked out.
     * Therefore we decrease the limitedFieldsCount
     */
    return values.map((v) => {
      if (v.value === currentValue) {
        return { ...v, count: v.count - 1 };
      }

      return v;
    });
  }

  function getMailValue() {
    const mailRegistrationField = registration?.registrationFields.find((field) => field.field.isEmailField);
    if (mailRegistrationField) return mailRegistrationField.value;
    if (registration?.user?.email) return registration?.user?.email;
    else if (user?._id === registration?.user) return user?.email;
    else return undefined;
  }

  useEffect(() => {
    fetchLimitedFieldsData();
  }, [event]);

  useEffect(() => {
    methods.reset(defaultValues);
  }, [registration]);

  useEffect(() => {
    /**
     * We assume that the first textvalue Option is always displayed
     */
    //TODO: Remove any cast
    if (!registration?.registrationFields) return;
    const firstTextValueOptions: any = registration?.registrationFields
      .filter(({ field }) => field.textValueOptions.length > 0)
      .map(({ field, value }) => [field._id, field.textValueOptions.find((tvo) => tvo.content === value)?.conditionalRequiredFields?.map((f) => f?._id || f)]);
    setRequiredFieldIds(new Map(firstTextValueOptions));
    //setRequiredFieldIds(conditionalRequiredFields.flat())
  }, [registration?.registrationFields]);

  useEffect(() => {
    onFormStateChange?.(methods.formState);
  }, [methods.formState.isDirty]);

  useEffect(() => {
    setEventRegistrationFields(
      event?.modules?.guestmanagement?.settings?.registrationFields
        ? event?.modules?.guestmanagement?.settings?.registrationFields
        : [...(registration?.possibleRegistrationFields || [])]
            //@ts-ignore
            .toSorted((a, b) => a.sortIndex - b.sortIndex)
    );
  }, [event, registration]);

  if (!registration || !eventRegistrationFields) return null;

  return (
    <FormProvider {...methods}>
      <form id="profilefields-mgmt" className="flex relative flex-col gap-2 w-full" onSubmit={methods.handleSubmit(submit)}>
        {eventRegistrationFields
          .filter((field) => filterRegistrationFields(field))
          .map((field) => {
            const match = registration.registrationFields.find((regField) => regField.field._id === field._id);
            const customValue = field.isEmailField ? getMailValue() : undefined;
            if (match) {
              return (
                <RegistrationFormField
                  fieldsTextColor={textColor}
                  key={`fields-${match.field._id}`}
                  field={match.field}
                  defaultValue={customValue ?? match.value}
                  disabled={(!match.field.canEditInProfile && !registration?.signup) || !!registration?.datetime_locked || methods.formState.isSubmitting}
                  requiredFieldIds={requiredFieldIds}
                  updateRequiredFieldIds={(fieldIds) => {
                    setRequiredFieldIds(fieldIds);
                  }}
                  limitedFieldsLoaded={!!limitedFieldsCount}
                  limitedValuesCount={getLimitedFieldsValuesCount(field, match.value)}
                  variant={textFieldVariant}
                  clickableFiles
                  showChild={isCurrentFieldVisible(match.field, eventRegistrationFields, methods.watch)}
                  onBlur={(key, value) => {
                    if (!autoSave) return;
                    methods.handleSubmit(() => submit({ [key]: value }))();
                  }}
                />
              );
            } else {
              return (
                <RegistrationFormField
                  fieldsTextColor={textColor}
                  key={`possible-fields-${field._id}`}
                  field={field}
                  defaultValue={customValue}
                  limitedFieldsLoaded={!!limitedFieldsCount}
                  limitedValuesCount={field.fieldType === 'limited' && limitedFieldsCount ? limitedFieldsCount.filter((f) => f._id === field._id) : []}
                  disabled={(!field.canEditInProfile && !registration?.signup) || !!registration?.datetime_locked || methods.formState.isSubmitting}
                  requiredFieldIds={requiredFieldIds}
                  updateRequiredFieldIds={(fieldIds) => setRequiredFieldIds(fieldIds)}
                  variant={textFieldVariant}
                  clickableFiles
                  showChild={!field.parentField?.field || methods.watch(field.parentField?.field) === field.parentField?.value}
                  onBlur={(key, value) => {
                    if (!autoSave) return;
                    methods.handleSubmit(() => submit({ [key]: value }))();
                  }}
                />
              );
            }
          })}
        {methods.formState.isDirty && !autoSave && (
          <VyButton showLoadingIcon={methods.formState.isSubmitting} disabled={methods.formState.isSubmitting} type="submit">
            {t('account:profilefields.save')}
          </VyButton>
        )}
      </form>
    </FormProvider>
  );
};
